@file:Suppress("DEPRECATION")
package org.example.newportfolio.components.sections.experience

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import org.jetbrains.compose.web.dom.Img
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import kotlinx.browser.window
import org.example.newportfolio.models.Experience
import org.example.newportfolio.models.ExperienceRole
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.fonts.*
import org.jetbrains.compose.web.css.*
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint

@Composable
private fun CardContent(experience: Experience) {
    var isHovered by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toPalette()
    val bp = rememberBreakpoint()
    Column(
        modifier = Modifier
            .then(
                if (bp >= Breakpoint.LG) Modifier.width(50.percent) else Modifier.fillMaxWidth().maxWidth(95.percent)
            )
            .padding(if (bp >= Breakpoint.LG) 28.px else 20.px)
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, palette.overlay)
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
            .thenIf(
                isHovered,
                Modifier
                    .transform { translateY((-6).px); scale(1.015) }
                    .boxShadow(color = palette.brand.primary.toRgb().copyf(alpha = 0.4f), blurRadius = 18.px, spreadRadius = 4.px)
            )
            .transition(
                Transition.of("transform", 0.28.s, TransitionTimingFunction.EaseInOut),
            )
    ) {
        // ─── Company Header ───────────────────────────────────────────────
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 6.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                experience.company,
                modifier = TextStyle.toModifier(ExperienceTitleTextStyle, TextStylePrimaryColor)
            )
            // Employment type pill
            Box(
                modifier = Modifier
                    .padding(leftRight = 10.px, topBottom = 4.px)
                    .borderRadius(20.px)
                    .backgroundColor(palette.brand.primary.toRgb().copyf(alpha = 0.15f))
            ) {
                SpanText(
                    experience.employmentType,
                    modifier = TextStyle.toModifier(ExperienceChipTextStyle, TextStylePrimaryColor)
                )
            }
        }
        // Location & total duration
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            SpanText(
                experience.location,
                modifier = TextStyle.toModifier(LabelMediumTextStyle, TextStyleSecondaryColor)
            )
            SpanText(
                experience.totalDuration,
                modifier = TextStyle.toModifier(LabelMediumTextStyle, TextStyleSecondaryColor)
            )
        }

        // ─── Roles ────────────────────────────────────────────────────────
        experience.roles.forEachIndexed { index, role ->
            if (index > 0) {
                // Divider between roles
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(palette.overlay)
                        .margin(topBottom = 16.px)
                )
            }
            RoleContent(role, bp)
        }

        // ─── Review Images (Freelance) ────────────────────────────────────
        if (experience.reviewImages.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(palette.overlay)
                    .margin(topBottom = 16.px)
            )
            SpanText(
                "Client Reviews & Testimonials",
                modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStylePrimaryColor)
                    .margin(bottom = 12.px)
            )
            Row(
                modifier = Modifier.fillMaxWidth().gap(12.px).flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Center
            ) {
                experience.reviewImages.forEach { imgPath ->
                    Box(
                        modifier = Modifier
                            .width(if (bp >= Breakpoint.MD) 30.percent else 100.percent)
                            .borderRadius(12.px)
                            .overflow(Overflow.Hidden)
                            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.2f))
                    ) {
                        Img(
                            src = imgPath,
                            attrs = Modifier.fillMaxWidth().objectFit(ObjectFit.Cover)
                                .toAttrs {
                                    attr("alt", "${experience.company} - Client review for Mohamed Elgohary")
                                    attr("loading", "lazy")
                                    attr("decoding", "async")
                                }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RoleContent(
    role: ExperienceRole,
    bp: Breakpoint
) {
    val palette = ColorMode.current.toPalette()
    // Role title & dates
    Column(modifier = Modifier.fillMaxWidth()) {
        SpanText(
            role.jobPosition,
            modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStylePrimaryColor)
                .margin(bottom = 4.px)
                .fontSize(if (bp >= Breakpoint.MD) 16.px else 14.px)
        )
        SpanText(
            "${role.from} - ${role.to} · ${Experience.formatRoleDuration(role.from, role.to)}",
            modifier = TextStyle.toModifier(ExperienceDateTextStyle, TextStyleSecondaryColor)
                .margin(bottom = 12.px)
        )

        // Description bullets
        Column(modifier = Modifier.margin(bottom = 14.px)) {
            role.description.split('\n')
                .map { it.trim() }
                .filter { it.isNotEmpty() }
                .forEach { line ->
                    Row(
                        modifier = Modifier.margin(bottom = 6.px),
                        verticalAlignment = Alignment.Top
                    ) {
                        SpanText(
                            "•  ",
                            modifier = TextStyle.toModifier(BodyLargeTextStyle, TextStyleSecondaryColor)
                        )
                        SpanText(
                            text = line,
                            modifier = TextStyle.toModifier(BodyLargeTextStyle, TextStylePrimaryColor)
                        )
                    }
                }
        }

        // Skills chips
        SimpleGrid(numColumns(base = 3, sm = 4, md = 5)) {
            role.skills.forEach { skill ->
                Box(
                    modifier = Modifier
                        .margin(top = 5.px, right = 5.px)
                        .padding(leftRight = 10.px, topBottom = 5.px)
                        .borderRadius(8.px)
                        .backgroundColor(palette.overlay)
                ) {
                    SpanText(skill, modifier = TextStyle.toModifier(ExperienceChipTextStyle, TextStylePrimaryColor))
                }
            }
        }

        // Project thumbnails under this role
        if (role.projects.isNotEmpty()) {
            SpanText(
                "Projects",
                modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStyleSecondaryColor)
                    .margin(top = 16.px, bottom = 8.px)
            )
            Row(
                modifier = Modifier.fillMaxWidth().gap(12.px).flexWrap(FlexWrap.Wrap)
            ) {
                role.projects.forEach { project ->
                    var projHovered by remember { mutableStateOf(false) }
                    Column(
                        modifier = Modifier
                            .width(if (bp >= Breakpoint.MD) 120.px else 100.px)
                            .padding(8.px)
                            .borderRadius(12.px)
                            .backgroundColor(palette.overlay)
                            .cursor(Cursor.Pointer)
                            .onMouseEnter { projHovered = true }
                            .onMouseLeave { projHovered = false }
                            .thenIf(
                                projHovered,
                                Modifier.transform { scale(1.05) }
                                    .boxShadow(color = palette.brand.primary.toRgb().copyf(alpha = 0.3f), blurRadius = 12.px, spreadRadius = 2.px)
                            )
                            .transition(Transition.of("transform", 0.2.s, TransitionTimingFunction.EaseInOut))
                            .onClick { window.open(project.link, "_blank") },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Img(
                            src = project.imageRes,
                            attrs = Modifier
                                .size(if (bp >= Breakpoint.MD) 64.px else 48.px)
                                .borderRadius(12.px)
                                .objectFit(ObjectFit.Cover)
                                .margin(bottom = 6.px)
                                .toAttrs {
                                    attr("alt", "${project.name} app logo")
                                    attr("loading", "lazy")
                                    attr("decoding", "async")
                                }
                        )
                        SpanText(
                            project.name,
                            modifier = TextStyle.toModifier(ExperienceChipTextStyle, TextStylePrimaryColor)
                                .textAlign(TextAlign.Center)
                                .fontSize(11.px)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ExperienceCard(experience: Experience) {
    val bp = rememberBreakpoint()
    val isOdd = experience.number.toInt() % 2 != 0
    Box(
        modifier = Modifier
            .id("experience-card-${experience.ordinal}")
            .fillMaxWidth()
            .position(Position.Relative)
            .padding(
                left = if (bp >= Breakpoint.LG) 50.px else 12.px,
                right = if (bp >= Breakpoint.LG) 50.px else 12.px,
                top = 20.px,
                bottom = 20.px
            )
            .classNames("experience-card"),
        contentAlignment = if (bp >= Breakpoint.LG) (if (isOdd) Alignment.CenterStart else Alignment.CenterEnd) else Alignment.Center
    ) {
        CardContent(experience)
    }
}